import { useState } from 'react';
import { Modal, ModalHeaderWithActions, useOpenClose } from '@evertrue/et-components';
import EmailSignaturesList from './email-signatures-list';
import EmailSignaturesForm from './email-signatures-form';
import PropTypes from 'prop-types';
import EmptyStateMessage from 'components/lists/empty-state-message-tv';
import AddSignatureModal from './add-signature-modal';
import { getLinkUrl } from 'entities/helpers/hardcodedLinks';
import { useEmailSignaturesProvider } from '../email-signatures-context';
import useFormatEmailBody from 'components/emailer/hooks/use-format-email-body';

const EmailSignaturesModal = ({ isOpen, onClose, contact, setFieldValue, emailBody }) => {
  const {
    signatures,
    selectedSignature,
    defaultSignature,
    setSelectedSignature,
    addEmailSignature,
    updateEmailSignature,
    deleteEmailSignature,
    setAsDefault,
    duplicateSignature,
  } = useEmailSignaturesProvider();

  const [isSaved, setIsSaved] = useState(true);
  const [isAddSigModalOpen, openAddSigModal, closeAddSigModal] = useOpenClose();
  const { parseBodyForExisitingSignature } = useFormatEmailBody();

  const handleAddSignature = async (signatureName, closeModal) => {
    try {
      await addEmailSignature(signatureName);
      closeModal();
    } catch {}
  };

  const handleUpdateDefaultSignature = async () => {
    const { id: prevDefaultId } = defaultSignature;
    const { id: selectedId } = selectedSignature;
    if (prevDefaultId !== selectedId) {
      await updateEmailSignature(selectedId === 0 ? prevDefaultId : selectedId, {
        default: selectedId !== 0,
      });
    }
    const updatedBody = parseBodyForExisitingSignature(emailBody, selectedSignature);
    setFieldValue('body', updatedBody);
    document.getElementsByClassName('jodit-wysiwyg')[0].innerHTML = updatedBody;
    onClose();
  };

  const updateSignature = async (id, updatedSignature) => {
    await updateEmailSignature(id, updatedSignature);
  };

  const handleDeleteSignature = async (signId) => {
    deleteEmailSignature(signId);
  };

  const handleClose = () => {
    setSelectedSignature(defaultSignature);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} closeModal={handleClose} size={signatures.length > 1 ? '1000px' : '850px'}>
      <>
        <ModalHeaderWithActions
          title="Signature"
          submitLabel="Done"
          closeModal={handleClose}
          onSubmit={handleUpdateDefaultSignature}
          disableSubmit={!isSaved || signatures.length <= 1}
          closeLabel="Cancel"
        />

        {signatures.length > 1 ? (
          <div style={{ display: 'flex' }}>
            <EmailSignaturesList
              defaultSignature={defaultSignature}
              selectedSignature={selectedSignature}
              setSelectedSignature={setSelectedSignature}
              signatures={signatures}
              handleAddSignature={handleAddSignature}
              updateSignature={updateSignature}
              handleDeleteSignature={handleDeleteSignature}
              handleDuplicateSignature={duplicateSignature}
              onSetDefault={setAsDefault}
              isModalOpen={isAddSigModalOpen}
              openModal={openAddSigModal}
              closeModal={closeAddSigModal}
            />
            {selectedSignature.id !== 0 && (
              <EmailSignaturesForm
                selectedSignature={selectedSignature}
                updateSignature={updateSignature}
                setIsSaved={setIsSaved}
                isSaved={isSaved}
                contact={contact}
              />
            )}
          </div>
        ) : (
          <EmptyStateMessage
            style={{ padding: '60px' }}
            icon="email-signature-icon"
            iconKit="gt2"
            actionLabel={'Add Signature'}
            onAction={isAddSigModalOpen ? closeAddSigModal : openAddSigModal}
            text="Your signature awaits."
            children={
              <>
                Add your contact details to give your emails a polished and consistent look. To learn more about Email
                Signatures visit our help center article:&nbsp;
                <a href={getLinkUrl('everTrue', 'emailsHelpCenterUrl')} target="_blank" rel="noreferrer">
                  Using Signal Email Templates and Signatures
                </a>
              </>
            }
          />
        )}
        <AddSignatureModal
          isOpen={isAddSigModalOpen}
          close={closeAddSigModal}
          handleSubmit={(signatureName) => handleAddSignature(signatureName, closeAddSigModal)}
        />
      </>
    </Modal>
  );
};

EmailSignaturesModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  contact: PropTypes.object,
  emailBody: PropTypes.string,
  setFieldValue: PropTypes.func,
};

export default EmailSignaturesModal;
